import React, { FC, useRef } from 'react';

import {
  MESSAGE__MAX_TITLE_LENGTH,
  MESSAGE__TITLE_REQUIRED,
} from '../../../../../constants/forms';
import { ComposeTitle } from '../../../../../containers/compose/props';

import translate from '../../../../../i18n/translate';

import TextInput from '../../../../MUI/TextInput';

import { getValidationResults } from './helpers';

type Props = {
  formId?: string;
  onChange: (value: ComposeTitle) => void;
  shouldFocus: boolean;
  shouldValidate?: boolean;
  value: ComposeTitle;
};

/**
 * Post title input
 *
 * @param props                Props passed to the component
 * @param props.formId         The formId, optional argument
 * @param props.onChange       Callback to be invoked every time input changes
 * @param props.shouldFocus    Whether the input should be focused
 * @param props.shouldValidate Whether the input should be validated
 * @param props.value          Current input value
 * @returns                    The component itself
 */
const ComposeMetaTitle: FC<Props> = ({
  formId,
  onChange,
  shouldFocus,
  shouldValidate = true,
  value,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  /**
   * Sanitize pasted content to prevent HTML injection
   *
   * @param input Input value
   */
  const handlePaste = (input: ComposeTitle) => {
    const el = document.createElement('div');
    el.innerHTML = input;

    onChange(el.textContent ?? el.innerText);
  };

  const { error, helperText } = getValidationResults(
    ref,
    shouldValidate,
    value,
  );

  return (
    <TextInput
      autoFocus={shouldFocus}
      enterKeyHint="send"
      externalError={error}
      externalHelperText={helperText}
      formId={formId}
      label={translate('GENERAL__TITLE')}
      maxLength={MESSAGE__MAX_TITLE_LENGTH}
      onChange={onChange}
      onPaste={handlePaste}
      placeholder={translate('TITLE__WRITE')}
      ref={ref}
      required={MESSAGE__TITLE_REQUIRED}
      value={value}
    />
  );
};

export default ComposeMetaTitle;
